export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  firstItemOnPage: number;
  lastItemOnPage: number;
}

/**
 * Backend enum values (numbers)
 * Used for POST requests
 */
export const SourceTypeEnum = {
  MediosAbiertos: 1,
  MediosSuscripcion: 2,
  RedesSociales: 3,
  GoogleTrends: 4,
  Comunicados: 5,
} as const;

export type SourceTypeEnum = (typeof SourceTypeEnum)[keyof typeof SourceTypeEnum];

/**
 * Backend string representation (returned by GET)
 */
export type SourceTypeString =
  | 'MediosAbiertos'
  | 'MediosSuscripcion'
  | 'RedesSociales'
  | 'GoogleTrends'
  | 'Comunicados';

/**
 * Backend social network enum values (numbers)
 * Required only for RedesSociales sources
 */
export const SocialNetworkEnum = {
  Facebook: 1,
  Instagram: 2,
  X: 3,
} as const;

export type SocialNetworkEnum = (typeof SocialNetworkEnum)[keyof typeof SocialNetworkEnum];

/**
 * Backend string representation of the social network (returned by GET)
 */
export type SocialNetworkString = 'Facebook' | 'Instagram' | 'X';

/**
 * Mapping from string representation (GET) to numeric enum value (POST/PUT)
 */
export const NETWORK_STRING_TO_ENUM: Record<SocialNetworkString, SocialNetworkEnum> = {
  Facebook: SocialNetworkEnum.Facebook,
  Instagram: SocialNetworkEnum.Instagram,
  X: SocialNetworkEnum.X,
};

/**
 * UI labels for social networks
 */
export const SOCIAL_NETWORK_LABELS: Record<SocialNetworkEnum, string> = {
  [SocialNetworkEnum.Facebook]: 'Facebook',
  [SocialNetworkEnum.Instagram]: 'Instagram',
  [SocialNetworkEnum.X]: 'X',
};

/**
 * Main Source entity - returned from GET /admin/sources
 * Backend returns type as string
 */
export interface Source {
  id: string;
  name: string;
  type: SourceTypeString;
  lastSyncAt: string | null;
  isEnabled: boolean;
  volume: number | null;
  latencyMs: number | null;
  coveragePercentage: number | null;
  regionIds: string[];
}

export interface SourcePaginationParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortAscending?: boolean;
}

/**
 * Request DTO for creating a source
 * Frontend sends type as number (enum value)
 */
export interface CreateSourceRequest {
  name: string;
  type: SourceTypeEnum;
  updateIntervalHours: number;
  regionIds: string[];
  isEnabled: boolean;
  baseUrl?: string;
  username?: string;
  password?: string;
  network?: SocialNetworkEnum;
  keywords?: string[];
}

/**
 * Response DTO for successful source creation
 */
export interface CreateSourceResponse {
  id: string;
  name: string;
  updateIntervalHours: number;
  regionIds: string[];
  isEnabled: boolean;
  createdAt: string;
}

/**
 * Mapping from numeric enum to string representation
 */
export const SOURCE_TYPE_MAP: Record<SourceTypeEnum, SourceTypeString> = {
  [SourceTypeEnum.MediosAbiertos]: 'MediosAbiertos',
  [SourceTypeEnum.MediosSuscripcion]: 'MediosSuscripcion',
  [SourceTypeEnum.RedesSociales]: 'RedesSociales',
  [SourceTypeEnum.GoogleTrends]: 'GoogleTrends',
  [SourceTypeEnum.Comunicados]: 'Comunicados',
};

/**
 * UI labels for source types (string based, from GET response)
 */
export const SOURCE_TYPE_LABELS: Record<SourceTypeString, string> = {
  MediosAbiertos: 'Medios Abiertos',
  MediosSuscripcion: 'Medios Suscripción',
  RedesSociales: 'Redes Sociales',
  GoogleTrends: 'Tendencias - Google Trends',
  Comunicados: 'Comunicados',
};

export const STRING_TO_SOURCE_TYPE_MAP: Record<SourceTypeString, SourceTypeEnum> = {
  MediosAbiertos: SourceTypeEnum.MediosAbiertos,
  MediosSuscripcion: SourceTypeEnum.MediosSuscripcion,
  RedesSociales: SourceTypeEnum.RedesSociales,
  GoogleTrends: SourceTypeEnum.GoogleTrends,
  Comunicados: SourceTypeEnum.Comunicados,
};

export interface SourceRegion {
  id: string;
  name: string;
  countryIds: string[];
  selector: number;
}

export interface SourceDetail {
  id: string;
  name: string;
  type: SourceTypeString;
  maskedUrl: string | null;
  credentialType: string | null;
  hasCredentials: boolean;
  hasHistoricalData?: boolean;
  regions: SourceRegion[];
  updateIntervalHours: number;
  isEnabled: boolean;
  createdAt: string;
  lastSyncAt: string | null;
  keywords: string[] | null;
  latencyMs: number | null;
  volume: number | null;
  coveragePercentage: number | null;
  network: SocialNetworkString | null;
}

export interface UpdateSourceRequest {
  name: string;
  type: SourceTypeEnum;
  updateIntervalHours: number;
  regionIds: string[];
  isEnabled: boolean;
  baseUrl?: string;
  username?: string;
  password?: string;
  network?: SocialNetworkEnum;
  keywords?: string[];
}

export interface UpdateSourceResponse {
  id: string;
  name: string;
  updateIntervalHours: number;
  regionIds: string[];
  isEnabled: boolean;
  updatedAt: string;
}

export interface ToggleSourceStatusRequest {
  enable: boolean;
}

export interface DeleteSourceRequest {
  reason: string;
}
